import { useState, useEffect } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Scale, Loader2, Eye, AlertTriangle, Clock, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { proceduresApi } from '../api/procedures';
import { CreateProcedureModal } from '../components/CreateProcedureModal';
import { PROCEDURE_LABELS } from '../constants/procedureTemplates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROCEDURE_STATUS_COLORS = {
    DRAFT: 'bg-gray-100 text-gray-700',
    ACTIVE: 'bg-blue-100 text-blue-700',
    SUSPENDED: 'bg-yellow-100 text-yellow-700',
    CLOSED: 'bg-green-100 text-green-700',
    ARCHIVED: 'bg-gray-100 text-gray-500'
};

const PROCEDURE_STATUS_LABELS = {
    DRAFT: 'Borrador',
    ACTIVE: 'Activo',
    SUSPENDED: 'Suspendido',
    CLOSED: 'Cerrado',
    ARCHIVED: 'Archivado'
};

const PROCEDURE_PRIORITY_COLORS = {
    LOW: 'bg-gray-100 text-gray-600',
    MEDIUM: 'bg-blue-100 text-blue-600',
    HIGH: 'bg-orange-100 text-orange-600',
    URGENT: 'bg-red-100 text-red-600'
};

const PROCEDURE_PRIORITY_LABELS = {
    LOW: 'Baja',
    MEDIUM: 'Media',
    HIGH: 'Alta',
    URGENT: 'Urgente'
};

export const Procedures = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [procedures, setProcedures] = useState([]);
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters from URL - Default to 'all' to avoid empty string issues in Select
    const [clientFilter, setClientFilter] = useState(searchParams.get('client_id') || 'all');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
    const [typeFilter, setTypeFilter] = useState(searchParams.get('procedure_type') || 'all');
    const [responsibleFilter, setResponsibleFilter] = useState(searchParams.get('responsible_user_id') || 'all');

    const [showCreateModal, setShowCreateModal] = useState(searchParams.get('create') === 'true');

    // Clear create param specifically if modal closes? 
    // Actually, onOpenChange handles close. We might want to clear URL param to avoid reopening on refresh, 
    // but standard behavior is fine for now or we can use setSearchParams.
    const handleModalOpenChange = (open) => {
        setShowCreateModal(open);
        if (!open) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('create');
            setSearchParams(newParams);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientFilter, statusFilter, typeFilter, responsibleFilter]);

    async function fetchData() {
        try {
            // Build filter object
            const filters = {};
            if (clientFilter && clientFilter !== 'all') filters.client_id = clientFilter;
            if (statusFilter && statusFilter !== 'all') filters.status = statusFilter;
            if (typeFilter && typeFilter !== 'all') filters.procedure_type = typeFilter;
            if (responsibleFilter && responsibleFilter !== 'all') filters.responsible_user_id = responsibleFilter;

            const [proceduresData, clientsRes, usersRes] = await Promise.all([
                proceduresApi.listProcedures(token, filters),
                axios.get(`${API}/clients`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { limit: 1000 }
                }),
                axios.get(`${API}/organization/users`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => ({ data: [] }))
            ]);

            const clientsData = (clientsRes.data && clientsRes.data.items) ? clientsRes.data.items : (Array.isArray(clientsRes.data) ? clientsRes.data : []);
            setProcedures(proceduresData);
            setClients(clientsData.filter(c => c.activo !== false));
            setUsers(usersRes.data.filter(u => u.activo !== false));
        } catch (error) {
            console.error('Error fetching procedures:', error);
            toast.error('Error al cargar procedimientos');
        } finally {
            setLoading(false);
        }
    }

    const handleFilterChange = (filterName, value) => {
        const newParams = new URLSearchParams(searchParams);

        if (value && value !== 'all') {
            newParams.set(filterName, value);
        } else {
            newParams.delete(filterName);
        }

        setSearchParams(newParams);

        // Update state
        switch (filterName) {
            case 'client_id':
                setClientFilter(value);
                break;
            case 'status':
                setStatusFilter(value);
                break;
            case 'procedure_type':
                setTypeFilter(value);
                break;
            case 'responsible_user_id':
                setResponsibleFilter(value);
                break;
            default:
                break;
        }
    };

    const clearFilters = () => {
        setSearchParams(new URLSearchParams());
        setClientFilter('all');
        setStatusFilter('all');
        setTypeFilter('all');
        setResponsibleFilter('all');
    };

    const hasActiveFilters =
        (clientFilter && clientFilter !== 'all') ||
        (statusFilter && statusFilter !== 'all') ||
        (typeFilter && typeFilter !== 'all') ||
        (responsibleFilter && responsibleFilter !== 'all');

    const getClientName = (clientId) => {
        const client = clients.find(c => c.id === clientId);
        return client ? client.nombre : clientId;
    };

    const isOverdue = (deadlineDate, status) => {
        if (!deadlineDate || status === 'CLOSED' || status === 'ARCHIVED') return false;
        const deadline = new Date(deadlineDate);
        const today = new Date();
        return deadline < today;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Sorting
    const [sortConfig, setSortConfig] = useState({ key: 'deadline_date', direction: 'asc' });

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedProcedures = [...procedures].sort((a, b) => {
        if (!sortConfig.key) return 0;

        let aValue = a[sortConfig.key] || '';
        let bValue = b[sortConfig.key] || '';

        // Handle dates
        if (sortConfig.key === 'deadline_date' || sortConfig.key === 'filing_date') {
            aValue = aValue ? new Date(aValue).getTime() : 0;
            bValue = bValue ? new Date(bValue).getTime() : 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    return (
        <>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900">Procedimientos</h2>
                        <p className="text-sm text-gray-500 mt-1">Gestión de procedimientos legales</p>
                    </div>
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800"
                    >
                        <Scale className="mr-2 h-4 w-4" />
                        Nuevo Procedimiento
                    </Button>
                </div>

                <CreateProcedureModal
                    open={showCreateModal}
                    onOpenChange={handleModalOpenChange}
                    initialClientId={clientFilter !== 'all' ? clientFilter : null}
                />

                {/* Filters */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Filter className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Filtros</span>
                        {hasActiveFilters && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={clearFilters}
                                className="ml-auto text-gray-500 hover:text-gray-700"
                            >
                                <X className="h-4 w-4 mr-1" />
                                Limpiar filtros
                            </Button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        {/* Client Filter */}
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Cliente</label>
                            <Select
                                value={clientFilter}
                                onValueChange={(value) => handleFilterChange('client_id', value)}
                                disabled={loading}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Todos los clientes" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los clientes</SelectItem>
                                    {clients.map(client => (
                                        <SelectItem key={client.id} value={client.id}>
                                            {client.nombre}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Status Filter */}
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Estado</label>
                            <Select
                                value={statusFilter}
                                onValueChange={(value) => handleFilterChange('status', value)}
                                disabled={loading}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Todos los estados" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los estados</SelectItem>
                                    <SelectItem value="DRAFT">Borrador</SelectItem>
                                    <SelectItem value="ACTIVE">Activo</SelectItem>
                                    <SelectItem value="SUSPENDED">Suspendido</SelectItem>
                                    <SelectItem value="CLOSED">Cerrado</SelectItem>
                                    <SelectItem value="ARCHIVED">Archivado</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Type Filter */}
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Tipo</label>
                            <Select
                                value={typeFilter}
                                onValueChange={(value) => handleFilterChange('procedure_type', value)}
                                disabled={loading}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Todos los tipos" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los tipos</SelectItem>
                                    {Object.entries(PROCEDURE_LABELS).map(([key, label]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Responsible User Filter */}
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Responsable</label>
                            <Select
                                value={responsibleFilter}
                                onValueChange={(value) => handleFilterChange('responsible_user_id', value)}
                                disabled={loading}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos</SelectItem>
                                    {users.map(u => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.nombre}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center">
                            <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
                        </div>
                    ) : procedures.length === 0 ? (
                        <div className="p-12 text-center">
                            <Scale className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay procedimientos</h3>
                            <p className="text-gray-500">
                                {hasActiveFilters
                                    ? 'No se encontraron procedimientos con los filtros aplicados'
                                    : 'No tienes procedimientos registrados en el sistema.'
                                }
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table View */}
                            <div className="hidden lg:block overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={() => requestSort('title')}
                                            >
                                                Título {getSortIcon('title')}
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Tipo
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Cliente
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Autoridad
                                            </th>
                                            <th
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={() => requestSort('deadline_date')}
                                            >
                                                Próximo Plazo {getSortIcon('deadline_date')}
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Prioridad
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Estado
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Acciones
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {sortedProcedures.map(procedure => {
                                            const overdue = isOverdue(procedure.deadline_date, procedure.status);
                                            return (
                                                <tr
                                                    key={procedure.id}
                                                    className="hover:bg-gray-100/50 transition-colors cursor-pointer"
                                                    onClick={() => navigate(`/procedures/${procedure.id}`)}
                                                >
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm font-medium text-gray-900">{procedure.title}</div>
                                                        <div className="text-[10px] text-gray-400 font-mono">{procedure.procedure_number}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                                                        {PROCEDURE_LABELS[procedure.procedure_type]}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {getClientName(procedure.client_id)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                        {procedure.authority || '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className={`flex items-center gap-1 text-sm ${overdue ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                                                            {procedure.deadline_date ? (
                                                                <>
                                                                    {overdue ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                                                                    <span>{formatDate(procedure.deadline_date)}</span>
                                                                </>
                                                            ) : '-'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <Badge className={`${PROCEDURE_PRIORITY_COLORS[procedure.priority]} border-0 text-[10px] shadow-none`}>
                                                            {PROCEDURE_PRIORITY_LABELS[procedure.priority]}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <Badge className={`${PROCEDURE_STATUS_COLORS[procedure.status]} border-0 text-[10px] shadow-none`}>
                                                            {PROCEDURE_STATUS_LABELS[procedure.status]}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                                        <Button variant="ghost" size="sm" className="text-gray-400 hover:text-blue-600">
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Card View */}
                            <div className="lg:hidden divide-y divide-gray-100">
                                {sortedProcedures.map(procedure => {
                                    const overdue = isOverdue(procedure.deadline_date, procedure.status);
                                    return (
                                        <div
                                            key={procedure.id}
                                            className="p-4 bg-white active:bg-gray-50 transition-colors cursor-pointer"
                                            onClick={() => navigate(`/procedures/${procedure.id}`)}
                                        >
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="flex-1 pr-2">
                                                    <h3 className="text-sm font-bold text-gray-900 leading-tight">{procedure.title}</h3>
                                                    <div className="text-[10px] text-gray-400 font-mono mt-0.5">{procedure.procedure_number || 'S/N'}</div>
                                                </div>
                                                <Badge className={`${PROCEDURE_STATUS_COLORS[procedure.status]} border-0 text-[10px] shadow-none h-5`}>
                                                    {PROCEDURE_STATUS_LABELS[procedure.status]}
                                                </Badge>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                                <div className="bg-gray-50 p-2 rounded-lg">
                                                    <span className="text-[10px] text-gray-400 uppercase font-bold block mb-1">Plazo</span>
                                                    <div className={`text-xs flex items-center gap-1 ${overdue ? 'text-red-600 font-bold' : 'text-gray-700 font-medium'}`}>
                                                        {procedure.deadline_date ? formatDate(procedure.deadline_date) : '-'}
                                                    </div>
                                                </div>
                                                <div className="bg-gray-50 p-2 rounded-lg">
                                                    <span className="text-[10px] text-gray-400 uppercase font-bold block mb-1">Prioridad</span>
                                                    <Badge className={`${PROCEDURE_PRIORITY_COLORS[procedure.priority]} border-0 text-[10px] h-4 px-1.5 shadow-none`}>
                                                        {PROCEDURE_PRIORITY_LABELS[procedure.priority]}
                                                    </Badge>
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-600 flex justify-between items-center border-t border-gray-50 pt-3">
                                                <span className="truncate max-w-[150px]">{getClientName(procedure.client_id)}</span>
                                                <span className="text-blue-600 font-bold flex items-center gap-1">Ver <Eye className="h-3 w-3" /></span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* Results count */}
                {!loading && procedures.length > 0 && (
                    <div className="text-sm text-gray-500 text-center pb-8">
                        Mostrando {procedures.length} procedimiento{procedures.length !== 1 ? 's' : ''}
                    </div>
                )}
            </div>
        </>
    );
};
